import React, { useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import './BasicSettings.css';
import logger from '../../../../../../utils/logger';


function BasicSettings({
  isEditMode,
  assistantName,
  setAssistantName,
  messagePrompt,
  setMessagePrompt,
  introMessageText,
  setIntroMessageText,
  customIntroMessage,
  setCustomIntroMessage,
  defaultLanguage,
  setDefaultLanguage,
  setInternalMessagePrompt,
  chatbotId,
  onSaveConfig,
  chatbotRolePrompt,
  botTagline,
  setBotTagline,
}) {
  const languages = useMemo(
    () => [
      { code: 'auto', name: 'Auto' },
      { code: 'en', name: 'English' },
      { code: 'es', name: 'Spanish' },
      { code: 'fr', name: 'French' },
      { code: 'de', name: 'German' },
      { code: 'zh', name: 'Chinese' },
      { code: 'ar', name: 'Arabic' },
      { code: 'hi', name: 'Hindi' },
    ],
    []
  );



  const updateConfig = useCallback(
    (key, value) => {
      const savedConfig = JSON.parse(localStorage.getItem('chatbotConfig')) || {};
      const updatedConfig = { ...savedConfig, [key]: value };

      localStorage.setItem('chatbotConfig', JSON.stringify(updatedConfig));

      if (onSaveConfig && chatbotId) {
        onSaveConfig(updatedConfig).catch((error) => {
          logger.error('Error saving configuration to backend:', error);
        });
      }
    },
    [chatbotId, onSaveConfig]
  );

  const getLanguageName = useCallback(
    (code) => {
      const language = languages.find((lang) => lang.code === code);
      return language ? language.name : 'Auto';
    },
    [languages]
  );

  useEffect(() => {
    // Only load from localStorage if NOT in edit mode
    if (!isEditMode) {
      const savedConfig = JSON.parse(localStorage.getItem('chatbotConfig')) || {};
      if (savedConfig.introMessageText) setIntroMessageText(savedConfig.introMessageText);
      if (savedConfig.assistantName) setAssistantName(savedConfig.assistantName);
      if (savedConfig.messagePrompt) setMessagePrompt(savedConfig.messagePrompt);
      if (savedConfig.defaultLanguage) setDefaultLanguage(savedConfig.defaultLanguage);
      if (savedConfig.introMessageText) setCustomIntroMessage(true);
      if (savedConfig.botTagline) setBotTagline(savedConfig.botTagline);

      if (!messagePrompt) {
        const defaultPrompt = "";
        setMessagePrompt(defaultPrompt);
        updateConfig('chatbotRolePrompt', defaultPrompt);
      }
    }
  }, [
    isEditMode,          // add isEditMode to dependencies
    setAssistantName,
    setMessagePrompt,
    setIntroMessageText,
    setCustomIntroMessage,
    setDefaultLanguage,
    setBotTagline,
    messagePrompt,
    defaultLanguage,
    updateConfig,
    setInternalMessagePrompt,
    getLanguageName,
  ]);

  const handleLanguageChange = (e) => {
    const selectedLanguage = e.target.value;
    setDefaultLanguage(selectedLanguage);
    updateConfig('defaultLanguage', selectedLanguage);

    const updatedPrompt =
      selectedLanguage === 'auto'
        ? chatbotRolePrompt
        : `${chatbotRolePrompt} Only Speak ${getLanguageName(selectedLanguage)} regardless of user's language.`;

    setInternalMessagePrompt(updatedPrompt);
    updateConfig('internalMessagePrompt', updatedPrompt);
  };

  const handleAssistantNameChange = (e) => {
    const newName = e.target.value;
    setAssistantName(newName);
    updateConfig('assistantName', newName);
  };

  const handleMessagePromptChange = (e) => {
    const newPrompt = e.target.value;
    setMessagePrompt(newPrompt);
    updateConfig('messagePrompt', newPrompt);
  };

  const handleIntroMessageChange = (e) => {
    const newMessage = e.target.value;
    setIntroMessageText(newMessage);
    setCustomIntroMessage(true);
    updateConfig('introMessageText', newMessage);
  };

  const handleBotTaglineChange = (e) => {
    const newTagline = e.target.value;
    setBotTagline(newTagline);
    updateConfig('botTagline', newTagline);
  };

  return (
    <div className="basic-settings-card">
      <form className="basic-settings-form">
        <div className="form-group">
          <label htmlFor="assistantName">Assistant Name</label>
          <small id="assistantNameHelp" className="form-text">
            Customize the name of your virtual assistant.
          </small>
          <input
            type="text"
            id="assistantName"
            placeholder="e.g., Bella"
            value={assistantName}
            onChange={handleAssistantNameChange}
            aria-describedby="assistantNameHelp"
          />
        </div>

        <div className="form-group">
          <label htmlFor="messagePrompt">Message Prompt</label>
          <small id="messagePromptHelp" className="form-text">
            Define the message input prompt.
          </small>
          <input
            type="text"
            id="messagePrompt"
            placeholder="Type your message and hit 'Enter'"
            value={messagePrompt}
            onChange={handleMessagePromptChange}
            aria-describedby="messagePromptHelp"
          />
        </div>

        <div className="form-group">
          <label htmlFor="introMessageText">Intro Message Text</label>
          <small id="introMessageTextHelp" className="form-text">
            Customize the introductory message displayed to users. To see changes immediately, clear the chat history using the trash icon in the chatbot.
          </small>
          <textarea
            id="introMessageText"
            placeholder="Hello! How can I help you today?"
            value={introMessageText}
            onChange={handleIntroMessageChange}
            aria-describedby="introMessageTextHelp"
          ></textarea>
        </div>

        <div className="form-group">
          <label htmlFor="defaultLanguage">Default Language</label>
          <small id="defaultLanguageHelp" className="form-text">
            Set the default language for your chatbot's responses. Select "Auto" to allow the chatbot to respond based on the user's language.
          </small>
          <select
            id="defaultLanguage"
            value={defaultLanguage}
            onChange={handleLanguageChange}
            className="language-select"
            aria-describedby="defaultLanguageHelp"
          >
            {languages.map((lang) => (
              <option key={lang.code} value={lang.code}>
                {lang.name}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="botTagline">Bot Tagline</label>
          <small id="botTaglineHelp" className="form-text">
            Customize the tagline for your chatbot.
          </small>
          <input
            type="text"
            id="botTagline"
            placeholder="e.g., Your friendly assistant"
            value={botTagline}
            onChange={handleBotTaglineChange}
            aria-describedby="botTaglineHelp"
          />
        </div>

      </form>
    </div>
  );
}

BasicSettings.propTypes = {
  isEditMode: PropTypes.bool,
  assistantName: PropTypes.string,
  setAssistantName: PropTypes.func,
  messagePrompt: PropTypes.string,
  setMessagePrompt: PropTypes.func,
  introMessageText: PropTypes.string,
  setIntroMessageText: PropTypes.func,
  customIntroMessage: PropTypes.bool,
  setCustomIntroMessage: PropTypes.func,
  defaultLanguage: PropTypes.string,
  setDefaultLanguage: PropTypes.func,
  setInternalMessagePrompt: PropTypes.func,
  chatbotId: PropTypes.string,
  onSaveConfig: PropTypes.func,
  chatbotRolePrompt: PropTypes.string,
  setChatbotRolePrompt: PropTypes.func,
  botTagline: PropTypes.string,
  setBotTagline: PropTypes.func,
};

export default BasicSettings;
